import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Signal } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { Observable, switchMap, tap } from 'rxjs';
import { API_CACHE_MODE_HEADER, EApiCacheMode } from '../../core/interceptors/api-interceptor.interceptor';
import { BaseDatatableStore, IBaseDatatableState } from '../../state/base-datatable-store.service';
import { ELoadStatus } from '../../state/state.interface';
import { IUser } from '../../state/user/user.interface';
import {
  HttpUtilitiesService,
  IBaseCrudResponse,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../utilities/http-utilities.service';
import { IDashboard } from '../settings/dashboards/dashboards.interface';
import { ITeam } from '../settings/users-teams/teams/teams.interface';
import {
  IEditSlideshow,
  ISlideshow,
  ISlideshowUserAssignmentUserJoin,
  IWorkspace,
  TAddSlideshow,
} from './slideshows.interface';

export interface ISlideshowState extends IBaseDatatableState<ISlideshow> {
  adminOptions: ISlideshowUserAssignmentUserJoin[];
  adminOptionsLoading: ELoadStatus;
  dashboards: IDashboard[];
  dashboardsLoading: ELoadStatus;
  teamOptions: ITeam[];
  teamOptionsLoading: ELoadStatus;
  userOptions: ISlideshowUserAssignmentUserJoin[];
  userOptionsLoading: ELoadStatus;
  workspaces: IWorkspace[];
  workspacesLoading: ELoadStatus;
}

@Injectable()
export class SlideshowsStore extends BaseDatatableStore<ISlideshow, ISlideshowState> {
  public readonly userOptions: Signal<ISlideshowUserAssignmentUserJoin[]> = this.selectSignal(
    (state: ISlideshowState) => state.userOptions,
  );

  public readonly adminOptions: Signal<ISlideshowUserAssignmentUserJoin[]> = this.selectSignal(
    (state: ISlideshowState) => state.adminOptions,
  );

  public readonly workspaces: Signal<IWorkspace[]> = this.selectSignal((state: ISlideshowState) => state.workspaces);

  public readonly teamOptions: Signal<ITeam[]> = this.selectSignal((state: ISlideshowState) => state.teamOptions);

  public readonly dashboardsOptions: Signal<IDashboard[]> = this.selectSignal(
    (state: ISlideshowState) => state.dashboards,
  );

  public readonly dashboardsLoading$: Observable<ELoadStatus> = this.select(
    (state: ISlideshowState) => state.dashboardsLoading,
  );

  public readonly userOptionsLoading$: Observable<ELoadStatus> = this.select(
    (state: ISlideshowState) => state.userOptionsLoading,
  );

  public readonly adminOptionsLoading$: Observable<ELoadStatus> = this.select(
    (state: ISlideshowState) => state.adminOptionsLoading,
  );

  public readonly teamOptionsLoading$: Observable<ELoadStatus> = this.select(
    (state: ISlideshowState) => state.teamOptionsLoading,
  );

  public readonly workspacesLoading$: Observable<ELoadStatus> = this.select(
    (state: ISlideshowState) => state.workspacesLoading,
  );

  readonly createOne = this.effect((trigger$: Observable<TAddSlideshow>) =>
    trigger$.pipe(
      switchMap((item: TAddSlideshow) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.createItem(item).pipe(
          tapResponse(
            (): void => {
              this.patchState({ singleCrudLoading: ELoadStatus.success });
            },
            // eslint-disable-next-line no-console,sonarjs/no-duplicate-string
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly updateOne = this.effect((trigger$: Observable<IEditSlideshow>) =>
    trigger$.pipe(
      switchMap((item: IEditSlideshow) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.updateItem(item).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteOne = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((id: number) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.deleteItem(id).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteBulk = this.effect((trigger$: Observable<number[]>) =>
    trigger$.pipe(
      switchMap((itemIds: number[]) => {
        this.patchState({ bulkCrudLoading: ELoadStatus.loading });

        return this.deleteItems(itemIds).pipe(
          tapResponse(
            (response): void => {
              this.patchState({ bulkCrudLoading: ELoadStatus.success, bulkOperationFailedData: response.data });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly resetDashboards = this.effect((trigger$) =>
    trigger$.pipe(
      tap(() => {
        this.patchState({
          dashboards: [],
          dashboardsLoading: ELoadStatus.success,
        });
      }),
    ),
  );

  readonly loadDashboards = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState({ dashboardsLoading: ELoadStatus.loading });
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getDashboards(httpParams).pipe(
          tapResponse(
            (output: IGetManyResponse<IDashboard>): void => {
              this.patchState({
                dashboards: output.data,
                dashboardsLoading: ELoadStatus.success,
              });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadItems = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ dataLoading: ELoadStatus.loading });

        return this.getItems(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<ISlideshow>) =>
              this.patchState({ data: response.data, dataLoading: ELoadStatus.success, dataTotal: response.total }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadAdminOptions = this.effect(
    (
      input$: Observable<{
        params: IGenericCrudRequestConstructionParameters;
        workspaceId: number;
      }>,
    ) =>
      input$.pipe(
        switchMap(({ params, workspaceId }) => {
          this.patchState({ adminOptionsLoading: ELoadStatus.loading });

          const httpParams = this.httpUtilities
            .insertGenericCrudRequestParameters(params)
            .set('workspaceId', workspaceId);

          return this.getUserDropdownOptions(httpParams).pipe(
            tapResponse(
              (output: IGetManyResponse<IUser>): void => {
                this.patchState({
                  adminOptions: output.data,
                  adminOptionsLoading: ELoadStatus.success,
                });
              },
              (error) => console.error('Error loading data', error),
            ),
          );
        }),
      ),
  );

  readonly loadUserOptions = this.effect(
    (
      input$: Observable<{
        params: IGenericCrudRequestConstructionParameters;
        workspaceId: number;
      }>,
    ) =>
      input$.pipe(
        switchMap(({ params, workspaceId }) => {
          this.patchState({ userOptionsLoading: ELoadStatus.loading });

          const httpParams = this.httpUtilities
            .insertGenericCrudRequestParameters(params)
            .set('workspaceId', workspaceId);

          return this.getUserDropdownOptions(httpParams).pipe(
            tapResponse(
              (output: IGetManyResponse<IUser>): void => {
                this.patchState({
                  userOptions: output.data,
                  userOptionsLoading: ELoadStatus.success,
                });
              },
              (error) => console.error('Error loading data', error),
            ),
          );
        }),
      ),
  );

  readonly loadTeamOptions = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState({ teamOptionsLoading: ELoadStatus.loading });
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getTeamDropdownOptions(httpParams).pipe(
          tapResponse(
            (output: IGetManyResponse<ITeam>): void => {
              this.patchState({
                teamOptions: output.data,
                teamOptionsLoading: ELoadStatus.success,
              });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadWorkspaces = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState({ workspacesLoading: ELoadStatus.loading });
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getWorkspaces(httpParams).pipe(
          tapResponse(
            (output: IGetManyResponse<IWorkspace>): void => {
              this.patchState({
                workspaces: output.data,
                workspacesLoading: ELoadStatus.success,
              });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      adminOptions: [],
      adminOptionsLoading: ELoadStatus.initial,
      bulkCrudLoading: ELoadStatus.initial,
      bulkOperationFailedData: [],
      dashboards: [],
      dashboardsLoading: ELoadStatus.initial,
      data: [],
      dataLoading: ELoadStatus.initial,
      dataTotal: 0,
      singleCrudLoading: ELoadStatus.initial,
      teamOptions: [],
      teamOptionsLoading: ELoadStatus.initial,
      userOptions: [],
      userOptionsLoading: ELoadStatus.initial,
      workspaces: [],
      workspacesLoading: ELoadStatus.initial,
    });
  }

  private createItem(body: TAddSlideshow): Observable<IBaseResponse<ISlideshow>> {
    return this.http.post<IBaseResponse<ISlideshow>>('slideshows', body);
  }

  private updateItem(editSlideshow: IEditSlideshow): Observable<IBaseResponse<ISlideshow>> {
    return this.http.patch<IBaseResponse<ISlideshow>>(`slideshows/${editSlideshow.id}`, editSlideshow.dto);
  }

  private deleteItem(id: number): Observable<IBaseResponse<ISlideshow>> {
    return this.http.delete<IBaseResponse<ISlideshow>>(`slideshows/${id}`);
  }

  private getItems(params: HttpParams): Observable<IGetManyResponse<ISlideshow>> {
    return this.http.get<IGetManyResponse<ISlideshow>>('slideshows', {
      headers: new HttpHeaders({ [API_CACHE_MODE_HEADER]: EApiCacheMode.none }),
      params,
    });
  }

  private deleteItems(ids: number[]): Observable<IBaseResponse<IBaseCrudResponse[]>> {
    return this.http.delete<IBaseResponse<IBaseCrudResponse[]>>('slideshows/bulk/delete', {
      body: { payload: ids },
    });
  }

  private getUserDropdownOptions(params: HttpParams): Observable<IGetManyResponse<IUser>> {
    return this.http.get<IGetManyResponse<IUser>>('users', { params });
  }

  private getTeamDropdownOptions(params: HttpParams): Observable<IGetManyResponse<ITeam>> {
    return this.http.get<IGetManyResponse<ITeam>>('teams', { params });
  }

  private getWorkspaces(params: HttpParams): Observable<IGetManyResponse<IWorkspace>> {
    return this.http.get<IGetManyResponse<IWorkspace>>('workspaces', { params });
  }

  private getDashboards(params: HttpParams): Observable<IGetManyResponse<IDashboard>> {
    return this.http.get<IGetManyResponse<IDashboard>>('dashboards', { params });
  }
}
